// Disable timeline controls and prevent iOS native controls
export function disableTimelineControls(player) {
  if (!player) return;

  // Set iOS-specific attributes to prevent fullscreen controls
  const videoEl = player.el()?.querySelector('video');
  if (videoEl) {
    // Modern iOS (10+) için
    videoEl.setAttribute('playsinline', 'true');
    // Eski iOS (9-) için fallback
    videoEl.setAttribute('webkit-playsinline', 'true');
  }

  // Disable seekbar interactions
  const progressControl = player.controlBar.getChild('ProgressControl');
  if (progressControl) {
    const seekBar = progressControl.getChild('SeekBar');

    if (seekBar) {
      // Save original functions for potential cleanup
      const events = [
        'MouseDown', 'MouseMove', 'MouseUp', 'Click',
        'TouchStart', 'TouchMove', 'TouchEnd'
      ];

      events.forEach(event => {
        const handler = `handle${event}`;
        seekBar[`_original${handler}`] = seekBar[handler];
      });

      // Block all interactions with preventDefault
      seekBar.handleMouseDown = seekBar.handleMouseMove =
          seekBar.handleMouseUp = seekBar.handleClick =
              seekBar.handleTouchStart = seekBar.handleTouchMove =
                  seekBar.handleTouchEnd = function(event) {
                    event.preventDefault();
                    event.stopPropagation();
                    event.stopImmediatePropagation();
                    return false;
                  };

      // Visual feedback - make it clear that timeline is disabled
      seekBar.el().style.cursor = 'default';
      seekBar.el().style.opacity = '0.6';
    }
  }

  // Mark as disabled for reference
  player._timelineDisabled = true;
}

// Optional: Function to re-enable timeline controls
export function enableTimelineControls(player) {
  if (!player || !player._timelineDisabled) return;

  const progressControl = player.controlBar.getChild('ProgressControl');
  if (progressControl) {
    const seekBar = progressControl.getChild('SeekBar');

    if (seekBar) {
      // Restore original functions
      const events = [
        'MouseDown', 'MouseMove', 'MouseUp', 'Click',
        'TouchStart', 'TouchMove', 'TouchEnd'
      ];

      events.forEach(event => {
        const handler = `handle${event}`;
        const originalHandler = seekBar[`_original${handler}`];
        if (originalHandler) {
          seekBar[handler] = originalHandler;
          delete seekBar[`_original${handler}`];
        }
      });

      // Restore visual style
      seekBar.el().style.cursor = '';
      seekBar.el().style.opacity = '';
    }
  }

  player._timelineDisabled = false;
}

export function getVideoType(url) {
  if (url.includes('.m3u8')) return 'application/x-mpegURL';
  if (url.includes('.webm')) return 'video/webm';
  if (url.includes('.ogg')) return 'video/ogg';
  return 'video/mp4';
}

export function isYouTubeUrl(url) {
  if (!url) return false;
  return /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)(\/|$)/i.test(url);
}

export function toYouTubeEmbedUrl(url) {
  if (!url) return '';

  let videoId = null;

  // youtube.com/embed/VIDEO_ID (zaten embed formatında)
  const embedMatch = url.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]+)/);
  if (embedMatch) return url;

  // youtube.com/watch?v=VIDEO_ID
  const watchMatch = url.match(/youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/);
  if (watchMatch) videoId = watchMatch[1];

  // youtu.be/VIDEO_ID
  if (!videoId) {
    const shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]+)/);
    if (shortMatch) videoId = shortMatch[1];
  }

  // youtube.com/v/VIDEO_ID
  if (!videoId) {
    const vMatch = url.match(/youtube\.com\/v\/([a-zA-Z0-9_-]+)/);
    if (vMatch) videoId = vMatch[1];
  }

  if (videoId) {
    return `https://www.youtube.com/embed/${videoId}`;
  }

  return url;
}

//hh:mm:ss to second
export function timeToSeconds(timeString) {
  if (typeof timeString === 'number') return timeString;
  if (!timeString) return 0;

  const parts = timeString.split(':');
  return parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
}

//seconds to 00:00
export function formatTime(seconds) {
  if (!seconds || isNaN(seconds)) return '00:00';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  } else {
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
}

/**
 * Page visibility setup for background pause
 * @param {Object} player - Video.js player instance
 * @param {Object} context - Vue component context (this)
 */
export function setupPageVisibility(player, context) {
  if (!player) return;

  const pausePlayer = () => {
    if (player && !player.paused()) {
      player.pause();
    }
  };

  // Tab değişimi için page visibility
  const handlePageVisibility = () => {
    if (document.hidden || !document.hasFocus()) {
      pausePlayer();
    }
  };

  // Tarayıcı penceresi focus kaybettiğinde video'yu durdur
  const handleWindowBlur = () => {
    if (!document.hasFocus()) {
      pausePlayer();
    }
  };

  // Video görünürlük kontrolü için Intersection Observer
  const handleIntersection = (entries) => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) {
        pausePlayer();
      }
    });
  };

  // Intersection Observer oluştur
  const intersectionObserver = new IntersectionObserver(handleIntersection, {
    threshold: 0.1
  });

  // Video container'ı gözlemle
  if (player && player.el()) {
    intersectionObserver.observe(player.el());
  }

  // Tab değişimi için event listener
  document.addEventListener('visibilitychange', handlePageVisibility);
  window.addEventListener('blur', handleWindowBlur);

  // Context'e referansları sakla
  context.intersectionObserver = intersectionObserver;
  context.pageVisibilityHandler = {
    visibilityChange: handlePageVisibility,
    windowBlur: handleWindowBlur
  };
}

/**
 * Cleanup page visibility handlers
 * @param {Object} context - Vue component context (this)
 */
export function cleanupPageVisibility(context) {
  // Intersection Observer'ı temizle
  if (context.intersectionObserver) {
    context.intersectionObserver.disconnect();
    context.intersectionObserver = null;
  }

  // Event listener'ı temizle
  if (context.pageVisibilityHandler && context.pageVisibilityHandler.visibilityChange) {
    document.removeEventListener('visibilitychange', context.pageVisibilityHandler.visibilityChange);
  }

  if (context.pageVisibilityHandler && context.pageVisibilityHandler.windowBlur) {
    window.removeEventListener('blur', context.pageVisibilityHandler.windowBlur);
  }

  context.pageVisibilityHandler = null;
}

/**
 * Video timeline'ına soru marker'ları ekler
 * @param {Object} player - Video.js player instance
 * @param {Array} questions - Soru listesi
 */
export function addQuestionMarkers(player, questions) {
  if (!player || !questions || questions.length === 0) return;

  // Marker'ları temizle
  if (player.markers) {
    player.markers.removeAll();
  }

  // DOM'un güncellenmesi için biraz daha uzun gecikme
  setTimeout(() => {
    // Her soru için marker oluştur
    questions.forEach((question, index) => {
      const timeInSeconds = timeToSeconds(question.time_in_video);

      const marker = {
        time: timeInSeconds,
        text: `Soru ${index + 1}`,
        overlayText: `Soru ${index + 1}`,
        class: 'question-marker',
        data: {
          questionId: question.id,
          questionIndex: index + 1
        }
      };

      // Marker'ı ekle
      if (player.markers) {
        player.markers.add([marker]);
      }
    });

    // Marker'ların görünür olduğunu kontrol et
    setTimeout(() => {
      const markers = player.el().querySelectorAll('.vjs-marker');

      // Marker'lara tıklama olaylarını engelle
      markers.forEach(marker => {
        marker.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }, true);
      });
    }, 200);
  }, 300);
}

/**
 * Video timeline'ından tüm marker'ları temizler
 * @param {Object} player - Video.js player instance
 */
export function clearQuestionMarkers(player) {
  if (!player || !player.markers) return;

  try {
    player.markers.removeAll();
    console.info('Tüm soru marker\'ları temizlendi');
  } catch (error) {
    console.warn('Marker temizleme sırasında hata:', error);
  }
}

/**
 * Aspect ratio'ya göre video boyutlandırma
 * @param {Object} player - Video.js player instance
 * @param {Object} container - Container element
 * @param {string} classPrefix - CSS class prefix (örn: 'video', 'player', 'interactive')
 */
export function adjustVideoSize(player, container, classPrefix = 'video') {
  if (!player || !container) return;

  const video = player.el().querySelector('video');
  if (!video || !video.videoWidth || !video.videoHeight) return;

  const aspectRatio = video.videoWidth / video.videoHeight;

  // Eski class'ları temizle
  container.classList.remove(
      `${classPrefix}-portrait`,
      `${classPrefix}-landscape`,
      `${classPrefix}-square`
  );

  // Aspect ratio'ya göre class ekle
  if (aspectRatio < 0.8) {
    // Dikey video (portrait)
    container.classList.add(`${classPrefix}-portrait`);
  } else if (aspectRatio > 1.2) {
    // Yatay video (landscape)
    container.classList.add(`${classPrefix}-landscape`);
  } else {
    // Kare video
    container.classList.add(`${classPrefix}-square`);
  }
}
